import dayjs from 'dayjs';
import type { ChatDocument } from '../../../../api/agents-client/agent-client.type';
import type { GoogleDriveFile } from '../../../../api/google-integrations-client/google-integrations-client.type';
import { getLabelForDateDifference } from '../../../../components/chat-history-panel/chat-history-panel.helpers';
import { getLocalCalendarDayDifference, parseToLocal, toLocalDate } from '../../../../helpers/date-time-helpers';
import { DOC_TYPE_CONFIG, formatFileSize } from '../../../flora/components/flora-document/flora-document.helpers';
import { getGoogleFilePreview } from './google-file-preview.helpers';

/** Where a unified document originated. */
type DocumentSource = 'flora' | 'google';

/** MIME type for Google Drive folders, excluded from the Documents list. */
const GOOGLE_FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

/**
 * A Flora-generated document and a connected Google Drive file normalized into
 * one shape so the Documents page can render them in a single combined list,
 * grid, or table. The original record is kept on `flora`/`google` for actions
 * (download, go-to-chat, preview, open-in-Google).
 */
interface UnifiedDocument {
  /** Source-prefixed id (`flora:<id>` / `google:<id>`) — unique across sources. */
  id: string;
  name: string;
  source: DocumentSource;
  /** Stable file-type key for the filter, e.g. `pdf`, `docx`, `gdoc`. */
  typeKey: string;
  typeLabel: string;
  typeColor: string;
  typeIcon: string;
  /** ISO timestamp used for sorting (Flora `created_at` / Google `modifiedTime`). */
  modifiedTime: string;
  /** Preformatted modified date for display. */
  modifiedLabel: string;
  /** Preformatted size (Flora only); `—` when unknown (Google files). */
  sizeLabel: string;
  /** Current revision number (Flora only), for the version badge + history. */
  revision?: number;
  /** Backend origin (Flora only): `flora_generated` | `uploaded` | … — gates preview. */
  floraSource?: string;
  flora?: ChatDocument;
  google?: GoogleDriveFile;
}

/** A file-type option for the multi-select filter. */
interface DocumentTypeOption {
  key: string;
  label: string;
  color: string;
}

/** Display order for known file types; unknown types are appended after these. */
const TYPE_ORDER = ['pdf', 'docx', 'xlsx', 'csv', 'tsv', 'txt', 'md', 'json', 'pptx', 'gdoc', 'gsheet', 'gslides'];

const NO_SIZE = '—';

/** Shown when a document's timestamp is missing or unparseable. */
const NO_DATE = '—';

/**
 * Parse a document timestamp to a local Dayjs, honoring its format so mixed
 * sources sort, label, and bucket consistently:
 * - Offset-bearing (Google Drive RFC-3339, e.g. `…Z` / `+00:00`) is read as its
 *   real instant, then localized — not re-shifted.
 * - UTC-naive (Flora `created_at`, no offset) is interpreted as UTC.
 *
 * `toLocalDate` alone (`.utc(true).local()`) would apply the local offset a
 * second time to an offset-bearing string, mis-dating Google files within an
 * offset of local midnight (e.g. a 00:30-local file bucketed as "Yesterday").
 *
 * @param {string} iso - The candidate timestamp.
 * @returns {dayjs.Dayjs | null} A local Dayjs, or null when missing/unparseable.
 */
function parseDocumentDate(iso: string): dayjs.Dayjs | null {
  if (!iso || !dayjs(iso).isValid()) {
    return null;
  }
  const hasExplicitOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso.trim());
  return hasExplicitOffset ? parseToLocal(iso) : toLocalDate(iso);
}

/**
 * Format a timestamp for display, or a neutral placeholder when it is missing or
 * unparseable (so a malformed value never renders as today).
 *
 * @param {string} iso - The candidate ISO timestamp.
 * @returns {string} The formatted date, or {@link NO_DATE} for invalid input.
 */
function formatModifiedLabel(iso: string): string {
  const parsed = parseDocumentDate(iso);
  return parsed ? parsed.format('MMM D, YYYY') : NO_DATE;
}

/**
 * Epoch milliseconds for sorting, or `-Infinity` for missing/unparseable
 * timestamps so they sort deterministically to the end of a most-recent-first
 * list rather than to the top.
 *
 * @param {string} iso - The candidate ISO timestamp.
 * @returns {number} Sort key in epoch millis, or `Number.NEGATIVE_INFINITY`.
 */
function sortableTime(iso: string): number {
  const parsed = parseDocumentDate(iso);
  return parsed ? parsed.valueOf() : Number.NEGATIVE_INFINITY;
}

/** Normalize a Flora-generated document. */
function fromFloraDocument(doc: ChatDocument): UnifiedDocument {
  const config = DOC_TYPE_CONFIG[doc.doc_type] ?? DOC_TYPE_CONFIG.pdf;
  return {
    id: `flora:${doc.id}`,
    name: doc.filename,
    source: 'flora',
    typeKey: doc.doc_type,
    typeLabel: config.label,
    typeColor: config.color,
    typeIcon: config.icon,
    modifiedTime: doc.created_at,
    modifiedLabel: formatModifiedLabel(doc.created_at),
    sizeLabel: doc.file_size != null ? formatFileSize(doc.file_size) : NO_SIZE,
    revision: doc.revision,
    floraSource: doc.source,
    flora: doc,
  };
}

/** Normalize a Google Drive file. */
function fromGoogleFile(file: GoogleDriveFile): UnifiedDocument {
  const preview = getGoogleFilePreview(file);
  return {
    id: `google:${file.id}`,
    name: file.name,
    source: 'google',
    typeKey: preview.typeKey,
    typeLabel: preview.label,
    typeColor: preview.color,
    typeIcon: preview.icon,
    modifiedTime: file.modifiedTime,
    modifiedLabel: formatModifiedLabel(file.modifiedTime),
    sizeLabel: NO_SIZE,
    google: file,
  };
}

/**
 * Merge Flora-generated documents and Google Drive files into one list,
 * excluding Google folders, sorted by most-recently-modified first.
 *
 * @param {ChatDocument[]} floraDocs - Flora-generated documents.
 * @param {GoogleDriveFile[]} googleFiles - Connected Google Drive files.
 * @returns {UnifiedDocument[]} The combined, folder-free, date-sorted list.
 */
function toUnifiedDocuments(floraDocs: ChatDocument[], googleFiles: GoogleDriveFile[]): UnifiedDocument[] {
  const unified: UnifiedDocument[] = [
    ...floraDocs.map(fromFloraDocument),
    ...googleFiles.filter((file) => file.mimeType !== GOOGLE_FOLDER_MIME_TYPE).map(fromGoogleFile),
  ];
  return unified.sort((a, b) => sortableTime(b.modifiedTime) - sortableTime(a.modifiedTime));
}

/**
 * Build the distinct file-type filter options present in the list, ordered by
 * {@link TYPE_ORDER} with any unknown types appended in first-appearance order.
 *
 * @param {UnifiedDocument[]} documents - The combined document list.
 * @returns {DocumentTypeOption[]} One option per distinct file type present.
 */
function buildTypeFilterOptions(documents: UnifiedDocument[]): DocumentTypeOption[] {
  const byKey = new Map<string, DocumentTypeOption>();
  for (const doc of documents) {
    if (!byKey.has(doc.typeKey)) {
      byKey.set(doc.typeKey, { key: doc.typeKey, label: doc.typeLabel, color: doc.typeColor });
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a.key);
    const bi = TYPE_ORDER.indexOf(b.key);
    return (ai === -1 ? TYPE_ORDER.length : ai) - (bi === -1 ? TYPE_ORDER.length : bi);
  });
}

/**
 * Filter documents to the selected file types. An empty selection means "all".
 *
 * @param {UnifiedDocument[]} documents - The combined document list.
 * @param {string[]} selectedTypeKeys - Selected `typeKey`s (empty = no filter).
 * @returns {UnifiedDocument[]} The documents matching the selection.
 */
function filterDocuments(documents: UnifiedDocument[], selectedTypeKeys: string[]): UnifiedDocument[] {
  if (selectedTypeKeys.length === 0) {
    return documents;
  }
  return documents.filter((doc) => selectedTypeKeys.includes(doc.typeKey));
}

/** A recency bucket of documents, labeled to match the Flora chat-history sidebar. */
interface DocumentRecencyGroup {
  label: string;
  documents: UnifiedDocument[];
}

/** Internal bucket, carrying a numeric key so groups can be ordered by recency. */
interface RecencyBucket extends DocumentRecencyGroup {
  /** Days since the bucket's newest document; `+Infinity` for the "Older" bucket. */
  sortKey: number;
}

/** Fallback bucket label for a document with a missing or unparseable timestamp. */
const UNDATED_GROUP_LABEL = 'Older';

/**
 * Resolve a document timestamp to its recency-bucket label and a numeric sort
 * key (days-ago), mirroring the Flora chat-history sidebar's boundaries.
 *
 * @param {string} modifiedTime - The document's ISO timestamp
 * @param {Date} now - Reference "now", passed in so a whole grouping pass shares one instant
 * @returns {{ label: string; daysDiff: number }} The bucket label (or "Older") and its days-ago key (`+Infinity` when undated)
 */
function recencyBucketForTimestamp(modifiedTime: string, now: Date): { label: string; daysDiff: number } {
  // Same parse as `formatModifiedLabel`/`sortableTime`, so a document's bucket
  // always agrees with the date shown on it and with its sort position.
  const parsed = parseDocumentDate(modifiedTime);
  if (!parsed) {
    return { label: UNDATED_GROUP_LABEL, daysDiff: Number.POSITIVE_INFINITY };
  }
  const localDate = parsed.toDate();
  // Local calendar-day boundaries (DST-safe midnight compare), matching the
  // history sidebar; clamp future timestamps to 0 so they read as "Today".
  const daysDiff = Math.max(0, getLocalCalendarDayDifference(localDate, now));
  return { label: getLabelForDateDifference(daysDiff, localDate), daysDiff };
}

/**
 * Group documents into recency buckets whose labels match the Flora chat-history
 * sidebar ("Today", "Yesterday", "N days ago", "Last week", "Last month",
 * "<Month Year>", …).
 *
 * Labels reuse {@link getLabelForDateDifference} for exact parity with the
 * sidebar. Documents keep their incoming (newest-first) order within a bucket,
 * and the buckets themselves are ordered by recency via a numeric key rather
 * than by first-appearance — so the group order is correct even if the caller's
 * sort (raw-instant) and a bucket label (local calendar day) disagree for a
 * document straddling local midnight. Unparseable timestamps collect in a
 * trailing "Older" bucket.
 *
 * @param {UnifiedDocument[]} documents - Documents, typically pre-sorted newest-first
 * @returns {DocumentRecencyGroup[]} Buckets ordered newest-first, each with its label and documents
 */
function groupDocumentsByRecency(documents: UnifiedDocument[]): DocumentRecencyGroup[] {
  const now = new Date();
  const buckets: RecencyBucket[] = [];
  for (const doc of documents) {
    const { label, daysDiff } = recencyBucketForTimestamp(doc.modifiedTime, now);
    const existing = buckets.find((bucket) => bucket.label === label);
    if (existing) {
      existing.documents.push(doc);
      // The bucket sorts by its newest (smallest days-ago) member.
      existing.sortKey = Math.min(existing.sortKey, daysDiff);
    } else {
      buckets.push({ label, sortKey: daysDiff, documents: [doc] });
    }
  }
  buckets.sort((a, b) => a.sortKey - b.sortKey);
  return buckets.map(({ label, documents: docs }) => ({ label, documents: docs }));
}

export { buildTypeFilterOptions, filterDocuments, groupDocumentsByRecency, sortableTime, toUnifiedDocuments };
export type { DocumentRecencyGroup, DocumentSource, DocumentTypeOption, UnifiedDocument };
